/**
 * StravaCard — panel de conexión Strava en la home.
 *
 * Encapsula los 4 estados de UX:
 *   1. NO conectado (nunca, o disconnected sin histórico) → CTA "Conectar".
 *   2. Conectado → resumen + botón "Sincronizar" + "Desconectar".
 *   3. Desconectado con histórico previo → CTA "Reconectar" + nota
 *      preservando el contador de actividades.
 *   4. **Importando** (Sprint C Bloque 2) → loader + counter incremental
 *      "Importando actividades… (X importadas)". Polling cada 3s a
 *      `/strava/import_status` hasta status terminal (completed|failed)
 *      o timeout de 60 polls (3 min).
 *
 * Polling se activa por `pollOnMount` prop (Home lo pasa true cuando detecta
 * `?strava=connected` en la URL del callback) o cuando el user pulsa
 * "Sincronizar" (la respuesta 202 dispara el polling automático).
 *
 * Cleanup: el setInterval se cancela en unmount Y cuando el polling
 * llega a estado terminal, para que el componente no quede polleando
 * indefinidamente si el user navega fuera.
 *
 * Cobertura de tests: src/__tests__/components/strava/StravaCard.test.tsx
 * + src/__tests__/flows/strava-flow.integration.test.tsx.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import * as stravaApi from "@/api/strava";
import { t } from "@/i18n/es";
import {
  ApiError,
  type StravaImportStatus,
  type StravaStatus,
} from "@/types/api";

interface Props {
  /**
   * Toast hook que la home pasa al componente para mostrar feedback
   * compartido (post-callback, post-sync, post-disconnect, errores).
   */
  showToast: (message: string, kind?: "success" | "error") => void;
  /**
   * Si true al montar, arranca polling inmediato a /strava/import_status
   * (caso post-callback OAuth: la home lo pone true cuando detecta
   * `?strava=connected` en la URL).
   */
  pollOnMount?: boolean;
}

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TICKS = 60; // 3 min total

function formatLastSync(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function pluralize(count: number, one: string, many: string, none?: string) {
  if (count === 0 && none) return none;
  if (count === 1) return one;
  return many.replace("{count}", String(count));
}

function translateStravaError(code: string): string {
  if (code === "strava_reauth_required") return t.strava.errorReauth;
  if (code === "strava_not_connected") return t.strava.errorGeneric;
  return t.strava.errorGeneric;
}

export function StravaCard({ showToast, pollOnMount = false }: Props) {
  const [status, setStatus] = useState<StravaStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busy, setBusy] = useState<"sync" | "disconnect" | "connect" | null>(
    null,
  );
  const [importJob, setImportJob] = useState<StravaImportStatus | null>(null);

  // Refs para el polling — usar refs en lugar de state evita
  // re-renders innecesarios y simplifica el cleanup.
  const pollIntervalRef = useRef<number | null>(null);
  const pollTickCountRef = useRef(0);
  const isMountedRef = useRef(true);

  const refreshStatus = useCallback(async () => {
    try {
      const s = await stravaApi.getStravaStatus();
      if (isMountedRef.current) setStatus(s);
    } catch {
      if (isMountedRef.current) {
        setStatus({
          connected: false,
          last_synced_at: null,
          activities_count: 0,
          disconnected_at: null,
        });
      }
    } finally {
      if (isMountedRef.current) setLoadingStatus(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollTickCountRef.current = 0;
  }, []);

  const handleTerminalJob = useCallback(
    (job: StravaImportStatus) => {
      stopPolling();
      if (job.status === "completed") {
        const msg = pluralize(
          job.activities_imported,
          t.strava.importToastOne,
          t.strava.importToastMany,
          t.strava.importToastZero,
        );
        showToast(msg, "success");
      } else if (job.status === "failed") {
        // Caso especial: refresh_token revocado → mensaje específico
        // de "vuelve a conectar".
        if (job.error_message === "strava_reauth_required") {
          showToast(t.strava.errorReauth, "error");
        } else {
          showToast(t.strava.importFailedToast, "error");
        }
      }
      // Refrescar el status para que el card refleje el resultado
      // (e.g. activities_count actualizado, conexión disconnected si
      // refresh_token revocado).
      void refreshStatus();
    },
    [refreshStatus, showToast, stopPolling],
  );

  const pollOnce = useCallback(async () => {
    pollTickCountRef.current += 1;
    try {
      const job = await stravaApi.getImportStatus();
      if (!isMountedRef.current) return;

      if (job === null) {
        // No hay job: nada que esperar, parar polling.
        stopPolling();
        setImportJob(null);
        return;
      }

      setImportJob(job);

      if (job.status === "completed" || job.status === "failed") {
        handleTerminalJob(job);
        return;
      }

      // Todavía pending|started — seguimos polleando hasta el cap.
      if (pollTickCountRef.current >= POLL_MAX_TICKS) {
        stopPolling();
        showToast(t.strava.importTimeoutWarning, "error");
      }
    } catch {
      // Si /import_status falla durante polling, no escalamos error
      // ruidoso — paramos polling silenciosamente y el user puede
      // refrescar manualmente.
      stopPolling();
    }
  }, [handleTerminalJob, showToast, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) return; // ya activo
    pollTickCountRef.current = 0;
    // Primer tick inmediato para feedback rápido, luego cada 3s.
    void pollOnce();
    pollIntervalRef.current = window.setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);
  }, [pollOnce]);

  // Mount: cargar status. Si pollOnMount, también arrancar polling.
  useEffect(() => {
    isMountedRef.current = true;
    void refreshStatus();
    if (pollOnMount) {
      startPolling();
    }
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intencional: solo en mount/unmount

  async function onConnect() {
    setBusy("connect");
    try {
      const { authorize_url } = await stravaApi.getStravaConnectUrl();
      window.location.assign(authorize_url);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "unknown_error";
      showToast(translateStravaError(code), "error");
      setBusy(null);
    }
  }

  async function onSync() {
    setBusy("sync");
    try {
      const result = await stravaApi.syncStrava();
      // 202 → empezamos polling para ver progreso/resultado.
      // El propio polling muestra el toast final cuando llega a terminal.
      setImportJob({
        job_id: result.job_id,
        task_id: "",
        status: result.status,
        job_type: "sync",
        activities_imported: 0,
        error_message: null,
        started_at: null,
        ended_at: null,
        created_at: null,
      });
      startPolling();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Ya hay un job activo; arrancamos polling sobre el existente.
        startPolling();
      } else {
        const code = err instanceof ApiError ? err.code : "unknown_error";
        showToast(translateStravaError(code), "error");
      }
    } finally {
      setBusy(null);
    }
  }

  async function onDisconnect() {
    setBusy("disconnect");
    try {
      await stravaApi.disconnectStrava();
      showToast(t.strava.disconnectedToast, "success");
      await refreshStatus();
    } catch {
      showToast(t.strava.errorGeneric, "error");
    } finally {
      setBusy(null);
    }
  }

  if (loadingStatus) {
    return (
      <section
        data-testid="strava-card-loading"
        aria-label={t.strava.sectionTitle}
        className="rounded-lg border border-border-subtle bg-carbon-raised p-4"
      >
        <p className="text-sm text-bone-muted">{t.strava.sectionTitle}…</p>
      </section>
    );
  }

  // Estado "importando" — tiene prioridad sobre conectado/no-conectado:
  // si hay un job activo (pending|started), mostramos el progreso aunque
  // técnicamente la conexión esté ya establecida.
  const isImporting =
    importJob !== null &&
    (importJob.status === "pending" || importJob.status === "started");

  if (isImporting) {
    return (
      <section
        data-testid="strava-card-importing"
        aria-label={t.strava.sectionTitle}
        aria-busy="true"
        className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-carbon-raised p-4"
      >
        <header className="flex items-center gap-2">
          <StravaLogo className="h-5 w-5" />
          <h2 className="text-sm font-medium uppercase tracking-widest text-bone">
            {t.strava.sectionTitle}
          </h2>
        </header>
        <div className="flex items-center gap-3">
          <span
            className="inline-block h-2 w-2 animate-pulse rounded-full bg-lime"
            aria-hidden="true"
          />
          <p className="text-sm text-bone">
            {pluralize(
              importJob.activities_imported,
              t.strava.importingOne,
              t.strava.importingMany,
              t.strava.importingZero,
            )}
          </p>
        </div>
      </section>
    );
  }

  const connected = status?.connected === true;
  const hasHistory = (status?.activities_count ?? 0) > 0;

  return (
    <section
      data-testid="strava-card"
      aria-label={t.strava.sectionTitle}
      className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-carbon-raised p-4"
    >
      <header className="flex items-center gap-2">
        <StravaLogo className="h-5 w-5" />
        <h2 className="text-sm font-medium uppercase tracking-widest text-bone">
          {t.strava.sectionTitle}
        </h2>
      </header>

      {connected ? (
        <>
          <p className="text-sm text-bone">
            {pluralize(
              status?.activities_count ?? 0,
              t.strava.connectedActivitiesOne,
              t.strava.connectedActivitiesMany,
              t.strava.connectedNoActivities,
            )}
          </p>
          {status?.last_synced_at && (
            <p className="text-xs text-bone-muted">
              {t.strava.lastSyncedAt.replace(
                "{when}",
                formatLastSync(status.last_synced_at),
              )}
            </p>
          )}
          <div className="mt-1 flex items-center gap-3">
            <button
              type="button"
              data-testid="strava-sync-btn"
              onClick={() => void onSync()}
              disabled={busy !== null}
              className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy === "sync" ? t.strava.syncing : t.strava.syncCta}
            </button>
            <button
              type="button"
              data-testid="strava-disconnect-btn"
              onClick={() => void onDisconnect()}
              disabled={busy !== null}
              className="text-xs text-bone-muted underline-offset-4 transition-colors duration-fast hover:text-bone hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.strava.disconnectCta}
            </button>
          </div>
        </>
      ) : (
        <>
          {hasHistory ? (
            <p className="text-sm text-bone-muted">
              {t.strava.disconnectedWithHistory.replace(
                "{count}",
                String(status?.activities_count ?? 0),
              )}
            </p>
          ) : (
            <p className="text-sm text-bone-muted">{t.strava.notConnectedHelp}</p>
          )}
          <button
            type="button"
            data-testid="strava-connect-btn"
            onClick={() => void onConnect()}
            disabled={busy !== null}
            style={{ backgroundColor: "#FC4C02" }}
            className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium text-white transition-opacity duration-fast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <StravaLogo className="h-4 w-4" filled="#FFFFFF" />
            {t.strava.connectCta}
          </button>
        </>
      )}
    </section>
  );
}

function StravaLogo({
  className = "h-4 w-4",
  filled = "#FC4C02",
}: {
  className?: string;
  filled?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill={filled}
      aria-hidden="true"
    >
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0 .5 13.828H4.67l5.792-5.599z" />
    </svg>
  );
}
